<template>
  <div class="wad4-video-toc-card" :class="{ 'is-selected': props.selected }" @click="seekToSection">
    <div class="wad4-video-toc-card__duration">
      <span class="toc-index">#{{ props.index + 1 }}</span>
      <span class="toc-time">{{ formattedMinutes }}</span>
    </div>
    <div v-if="props.selected" class="wad4-video-toc-card__tracker-wrapper">
      <div  class="wad4-video-toc-card__tracker-bg"></div>
      <div
          class="wad4-video-toc-card__tracker-fg"
          :style="{ width: progress + '%' }"
      ></div>
    </div>
    <h3 class="wad4-video-toc-card__title">{{ section.title }}</h3>
    <p class="wad4-video-toc-card__description">{{ section.description }}</p>
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue'
import type {VideoSection} from "~/types/video";

const props = defineProps<{
  section: VideoSection;
  index: number;
  onSeek?: (timestamp: number, index: number) => void;
  selected?: boolean;
  currentTime: number;
}>();


const formattedMinutes = computed(() => {
  const minutes = Math.round(props.section.duration_ms / 1000 / 60);
  return `about ${minutes} minute${minutes !== 1 ? 's' : ''}`;
});

function seekToSection() {
  props.onSeek?.(props.section.start_ms, props.index);
}

const progress = computed(() => {
  const start = props.section.start_ms / 1000;
  const end = start + props.section.duration_ms / 1000;

  if (props.currentTime < start) return 0;
  if (props.currentTime >= end) return 100;

  const played = props.currentTime - start;
  const duration = end - start;
  return Math.min((played / duration) * 100, 100);
});
</script>

<style lang="scss">

.wad4-video-toc-card {
  flex: 0 0 auto;
  scroll-snap-align: start;
  width: 280px;
  height: auto;
  padding: $wad4-spacing-xl;
  display: flex;
  flex-direction: column;
  border-radius: $wad4-border-radius-md;
  border: 1px solid $wad4-c-border-border-secondary;
  background: $wad4-c-background-bg-primary;

  &:hover {
    border-color: $wad4-c-border-border-brand-solid;
    background-color: $wad4-c-background-bg-primary-hover;
    cursor: pointer;

    .wad4-video-toc-card__title {
      color: $wad4-c-text-text-primary-active;
    }
  }

  &.is-selected {
    border-color: $wad4-c-border-border-brand-solid;
    background-color: $wad4-c-background-bg-primary-active;

    .wad4-video-toc-card__title {
      color: $wad4-c-text-text-primary-active;
      margin-top: unset;
    }

    .wad4-video-toc-card__description {
      color: $wad4-c-text-text-secondary;
    }

    .wad4-video-toc-card__tracker-wrapper {
      margin-top: 6px;
      margin-bottom: 6px;
    }

    .wad4-video-toc-card__duration{
      margin: unset;
    }
  }

  &__tracker-wrapper {
    position: relative;
    height: 4px;
    width: 100%;
    border-radius: 4px;
    margin-top: -4px;
    margin-bottom: -4px;
    background-color: transparent;
  }

  &__tracker-bg {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: $wad4-c-border-border-brand;
    border-radius: 4px;
  }

  &__tracker-fg {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: $wad4-c-border-border-brand-solid;
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  &__duration {
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;

    .toc-index {
      color: $wad4-c-text-text-brand-tertiery;
      font-family: $wad4-default-font-mono;
    }

    .toc-time {
      color: $wad4-c-text-text-tertiery;
    }
  }

  &__title {
    overflow: hidden;
    color: $wad4-c-text-text-primary;
    text-overflow: ellipsis;
    font-size: 16px;
    font-style: normal;
    font-weight: $wad4-font-weight-regular-bold;
    line-height: 22px;
    margin-bottom: 8px;
    margin-top: 0;
  }

  &__description {
    overflow: hidden;
    color: $wad4-c-text-text-secondary;
    text-overflow: ellipsis;
    font-size: 14px;
    margin: unset;
  }
}
</style>
